import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { AppItem, ColorAliases } from '@mds/common';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import {
  AppHeaderComponent,
  AppItemComponent,
  ButtonComponent,
  ButtonOptions,
  LoaderModule,
  MenuNavBlockComponent
} from '@mds/ui';
import { MenuService } from '@services/menu.service';

export interface SubGroup {
  title: string;
  items: AppItem[];
  icon?: MdsIcon;
}

export interface ItemList {
  items: SubGroup[];
  title: string;
  limit?: number;
  isExpanded?: boolean;
  enableFavouriteButton?: boolean;
  isFavorite?: boolean;
  showSubGroupTitles?: boolean;
}

@Component({
  selector: 'app-apps-menu',
  imports: [
    AppHeaderComponent,
    AppItemComponent,
    ButtonComponent,
    CommonModule,
    LoaderModule,
    MatButtonToggleModule,
    MatMenuModule,
    MdsIconsModule,
    MenuNavBlockComponent
  ],
  templateUrl: './apps-menu.html',
  styleUrl: './apps-menu.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppsMenuComponent implements OnInit {
  protected mdsIcon = MdsIcon;
  protected colorAliases = ColorAliases;

  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;

  private menuService = inject(MenuService);
  private destroyRef = inject(DestroyRef);
  private changeDetectionRef = inject(ChangeDetectorRef);

  @Input() activeApp = null;
  private _customItemsList: ItemList[];

  @Input()
  set customItemsList(value: ItemList[]) {
    if (value) {
      this._customItemsList = value;
      this.itemsList = value;
      this.filterItems();
    }
  }

  get customItemsList(): ItemList[] {
    return this._customItemsList;
  }

  @Input() searchPlaceholder = 'Start typing here to search for app';
  @Input() noResultsText = 'No Data found';

  @Output() appClick: EventEmitter<AppItem> = new EventEmitter<AppItem>();

  itemsList: ItemList[] = [];
  filteredItemsList: ItemList[] = [];
  subGroupToggleMenu: Partial<ButtonOptions>[] = [];
  searchText = '';
  selectedSubGroup = '';
  icons: Map<string, MdsIcon> = new Map();
  loadersToShow: string[] = [];

  ngOnInit(): void {
    if (this.customItemsList) {
      this.itemsList = this.customItemsList;
      this.filterItems();
    } else {
      this.fetchApps();
    }
  }

  private fetchApps(): void {
    this.menuService
      .getAppsAndFavoriteApps()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((appsAndFavoriteApps) => {
        this.itemsList = this.mapAppsToItemList(appsAndFavoriteApps);
        this.filterItems();
        this.buildSubGroupToggleMenu(this.itemsList[1].items);
      });
  }

  buildSubGroupToggleMenu(groups: SubGroup[]): void {
    this.subGroupToggleMenu = groups.map((item) => ({
      icon: item.icon,
      label: item.title,
      value: item.title
    }));
  }

  onFavoriteValueChange(newValue: boolean, rid: string): void {
    this.loadersToShow.push(rid);

    const addOrRemoveFavorite = newValue
      ? this.menuService.addFavoriteApi({ apps: { rids: [rid] } })
      : this.menuService.removeFavorite([rid]);

    addOrRemoveFavorite.subscribe(() => {
      this.menuService.getFavoriteApps().subscribe(() => {
        const index = this.loadersToShow.findIndex((r) => r === rid);
        this.loadersToShow.splice(index, 1);
        this.fetchApps();
      });
    });
  }

  onSearch(searchText: string): void {
    this.searchText = searchText;
    this.filterItems();
  }

  onSubGroupChange(newValue: string): void {
    this.selectedSubGroup = newValue === this.selectedSubGroup ? '' : newValue;
    this.filterItems();
  }

  filterItems(): void {
    const itemsToFilter = this.getFilteredItemsBySubGroupCategory();
    this.filteredItemsList = this.filterItemsBySearchText(itemsToFilter);
    this.changeDetectionRef.markForCheck();
  }

  getFilteredItemsBySubGroupCategory(): ItemList[] {
    return this.selectedSubGroup
      ? this.itemsList
          .map((group) => {
            const filteredSubGroups = group.items
              .map((subGroup) => {
                if (group.isFavorite) {
                  const filteredItems = subGroup.items.filter((item) => item.category === this.selectedSubGroup);
                  return filteredItems.length ? { ...subGroup, items: filteredItems } : null;
                }
                return subGroup.title === this.selectedSubGroup ? subGroup : null;
              })
              .filter((x) => !!x);

            return filteredSubGroups.length
              ? {
                  ...group,
                  items: filteredSubGroups,
                  title: group.isFavorite ? `Favorite ${this.selectedSubGroup} apps` : `${this.selectedSubGroup} apps`
                }
              : null;
          })
          .filter((x) => !!x)
      : this.itemsList;
  }

  filterItemsBySearchText(itemsToFilter: ItemList[]): ItemList[] {
    return this.searchText
      ? itemsToFilter
          .map((group) => {
            const filteredSubGroups = group.items
              .map((subGroup) => {
                const filteredItems = subGroup.items.filter((item) =>
                  item.app_name?.toLowerCase().includes(this.searchText?.toLowerCase())
                );
                return filteredItems.length ? { ...subGroup, items: filteredItems } : null;
              })
              .filter((x) => !!x);

            return filteredSubGroups.length ? { ...group, items: filteredSubGroups } : null;
          })
          .filter((x) => !!x)
      : itemsToFilter;
  }

  getAppIcon(item: AppItem): MdsIcon {
    if (this.icons.has(item.icon_app)) {
      return this.icons.get(item.icon_app);
    }

    const fileName = item.icon_app?.split('/')?.pop()?.split('.')[0].replace(/[-_]/g, '')?.toLowerCase();
    let appIcon: MdsIcon = MdsIcon.APP_IMPORTER_DEFAULT;

    if (fileName) {
      const match = Object.entries(MdsIcon).find(([key, value]) => {
        const transformedKey = key.replace('APP_', '').toLowerCase().replace(/[-_]/g, '');
        return transformedKey === fileName || value === fileName;
      });

      if (match) {
        appIcon = MdsIcon[match[0]];
      }
    }

    this.icons.set(item.icon_app, appIcon);
    return appIcon;
  }

  isItemLoading(rid: string): boolean {
    return !!this.loadersToShow.find((r) => r === rid);
  }

  onItemClick(item: AppItem): void {
    this.appClick.emit(item);
    this.trigger.closeMenu();
  }

  onMenuClosed(): void {
    this.resetItemsAndFilters();
  }

  resetItemsAndFilters(): void {
    this.searchText = '';
    this.selectedSubGroup = '';
    this.filteredItemsList = this.itemsList;
    this.changeDetectionRef.markForCheck();
  }

  mapAppsToItemList(appsAndFavoriteApps): ItemList[] {
    return [
      {
        items: appsAndFavoriteApps?.favoriteApps?.length
          ? [{ items: appsAndFavoriteApps.favoriteApps, title: '' }]
          : [],
        title: 'Favorite apps',
        limit: 3,
        enableFavouriteButton: true,
        isFavorite: true
      },
      {
        items: appsAndFavoriteApps.appData
          ?.sort((a, b) => a?.category?.localeCompare(b?.category))
          .map((group) => {
            return {
              items: group.list,
              title: group.category,
              icon: group.icon
            };
          }),
        title: 'All apps',
        enableFavouriteButton: true,
        isFavorite: false,
        showSubGroupTitles: false
      }
    ];
  }
}
